// useTodos — single-list view over the multi-list server backing.
//
// The server supports many lists per user, but the To-do section of the
// new UI is intentionally one-list-only (matching the design). Strategy:
//   - On mount, fetch lists. If none, create a "Todos" list. Otherwise
//     pick the first non-archived list.
//   - Track items for that list. add/update/remove all hit RPCs and
//     update local state optimistically.
//
// Server-side alarm scheduling fires automatically when an item has
// dueAt + alarmEnabled = true. Toggling alarmEnabled off or clearing
// dueAt cancels the alarm; the server handles all the re-scheduling
// math, the client just sets the flags.

import { useCallback, useEffect, useState } from "preact/hooks";
import { api, isOfflineError, peekCache, type TodoItem, type TodoList } from "./rpc";

const DEFAULT_LIST_NAME = "Todos";

export interface UseTodos {
  list: TodoList | null;
  items: TodoItem[];
  loading: boolean;
  error: string | null;
  add: (title: string) => Promise<void>;
  toggle: (id: string) => Promise<void>;
  update: (id: string, patch: Partial<TodoItem>) => Promise<void>;
  remove: (id: string) => Promise<void>;
  clearDone: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function useTodos(): UseTodos {
  const [list, setList] = useState<TodoList | null>(null);
  const [items, setItems] = useState<TodoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);

    // SWR: paint cached list + items immediately so the section opens
    // without a spinner. Network call below replaces with fresh data.
    let hadCache = false;
    try {
      const cachedLists = await peekCache<{ ok: true; lists: TodoList[] }>("todos.listLists");
      const cachedActive = cachedLists?.lists?.find((l) => !l.archived) || null;
      if (cachedActive) {
        hadCache = true;
        setList(cachedActive);
        const cachedItems = await peekCache<{ ok: true; items: TodoItem[] }>("todos.listItems", { listId: cachedActive.id });
        if (cachedItems?.items) setItems(cachedItems.items);
        setLoading(false);
      }
    } catch { /* swallow */ }
    if (!hadCache) setLoading(true);

    try {
      // Resolve a working list — first non-archived OR create one.
      let lists = await api.todoLists();
      let active = lists.find((l) => !l.archived) || null;
      // Only auto-create when the server (or cache fallback) returns
      // an empty list. Offline + archived-only would otherwise fire
      // todos.createList — which has no write policy, so it'd throw
      // "failed to fetch" on top of perfectly good cached state.
      if (!active && lists.length === 0) {
        active = await api.todoCreateList(DEFAULT_LIST_NAME);
        lists = [active];
      }
      if (active) {
        setList(active);
        const fetched = await api.todoListItems(active.id);
        setItems(fetched);
      }
    } catch (e: any) {
      if (!hadCache || !isOfflineError(e)) {
        setError(e?.message || "failed to load todos");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const add = useCallback(async (title: string) => {
    if (!list) return;
    const trimmed = title.trim();
    if (!trimmed) return;
    try {
      const item = await api.todoAdd({ listId: list.id, title: trimmed });
      setItems((prev) => [...prev, item]);
    } catch (e: any) { setError(e?.message || "add failed"); }
  }, [list]);

  const toggle = useCallback(async (id: string) => {
    const cur = items.find((i) => i.id === id);
    if (!cur) return;
    // Optimistic flip.
    setItems((prev) => prev.map((i) => i.id === id ? { ...i, done: !i.done } : i));
    try {
      const item = await api.todoUpdate(id, { done: !cur.done });
      setItems((prev) => prev.map((i) => i.id === id ? item : i));
      // Recurring completion spawns a new occurrence server-side;
      // the response only carries the just-completed row. Refresh
      // so the spawned clone shows up in the list immediately.
      if (cur.rrule && !cur.done) {
        refresh();
      }
    } catch (e: any) {
      // Roll back on failure.
      setItems((prev) => prev.map((i) => i.id === id ? cur : i));
      setError(e?.message || "toggle failed");
    }
  }, [items, refresh]);

  const update = useCallback(async (id: string, patch: Partial<TodoItem>) => {
    const cur = items.find((i) => i.id === id);
    setItems((prev) => prev.map((i) => i.id === id ? { ...i, ...patch } : i));
    try {
      const item = await api.todoUpdate(id, patch);
      setItems((prev) => prev.map((i) => i.id === id ? item : i));
      // Same spawn-on-complete refresh as toggle(): if this patch
      // flipped done true on a recurring row, pick up the new clone.
      if (cur && cur.rrule && !cur.done && patch.done === true) {
        refresh();
      }
    } catch (e: any) {
      setError(e?.message || "update failed");
      // Caller can refresh() to re-sync if they care; we don't roll
      // back partial patches here because mid-edit churn would be
      // jumpier than just letting the server-side state win on next
      // load.
    }
  }, [items, refresh]);

  const remove = useCallback(async (id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
    try { await api.todoDelete(id); }
    catch (e: any) { setError(e?.message || "delete failed"); }
  }, []);

  const clearDone = useCallback(async () => {
    if (!list) return;
    try {
      await api.todoClearDone(list.id);
      setItems((prev) => prev.filter((i) => !i.done));
    } catch (e: any) { setError(e?.message || "clear failed"); }
  }, [list]);

  return { list, items, loading, error, add, toggle, update, remove, clearDone, refresh };
}
